"use client";

import { FC, useEffect, useState } from "react";

import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import Conditional from "@repo/shared-ui/Conditional";
import { doesExist, getSessionData } from "@repo/shared-utils/common-utils";
import { formatDateTime } from "@repo/shared-utils/num-utils";
import { stringToBoolean } from "@repo/shared-utils/pwa-utils";
import {
  LeadStatus,
  selectIsAuthSuccessOrPerpetual,
  setLeadStatus,
  useAppDispatch,
  useAppSelector,
} from "@repo/web-store";
import { IStockDetailsData } from "../../interfaces/StockDetails.interface";
import {
  IStockInfoContent,
  IStockInfoData,
} from "../../interfaces/StockInfo.interface";
import DematAccount from "../Demat/DematAccount";
import SwitchExchange from "../SwitchExchange/SwitchExchange";
interface StockInfoProps {
  content: IStockInfoContent;
  data: { stockDetailsData: IStockDetailsData; stockInfoData: IStockInfoData };
  nseToken: number;
  bseToken: number;
  exchId: string;
  formattedSymbol: string;
}

const StockInfo: FC<StockInfoProps> = ({
  content,
  data,
  nseToken,
  bseToken,
  exchId,
  formattedSymbol,
}) => {
  const [loading, setLoading] = useState(false);
  const appDispatch = useAppDispatch();
  const isAuthSuccessOrPerpetual = useAppSelector(
    selectIsAuthSuccessOrPerpetual,
  );

  const { stockDetailsData, stockInfoData } = data || {};
  const { compName, symbol, ind } = stockInfoData || {};
  const { mktCapType, price, chg, chgPer, ltt } = stockDetailsData || {};

  const chgSymbol = chg >= 0 ? "+" : "";
  const chgColor = chg >= 0 ? "green-600" : "red-600";

  useEffect(() => {
    if (!isAuthSuccessOrPerpetual) {
      console.warn(
        "Not fetching lead status as user is not logged in or perpetual",
      );
      return;
    }
    const getLeadsData = async () => {
      const { leadsBuySell } = await import("@repo/sso/leadsApi");
      const { mobileNo } = await getSessionData();
      if (mobileNo) {
        const leadsData = await leadsBuySell(mobileNo);
        if (leadsData?.statusCode === 90) {
          appDispatch(
            setLeadStatus({ leadStatus: leadsData.data?.flag as LeadStatus }),
          );
        } else {
          appDispatch(setLeadStatus({ leadStatus: "" as LeadStatus }));
        }
      }
    };

    getLeadsData();
  }, [isAuthSuccessOrPerpetual]);

  return (
    <div className="mb-5 flex flex-col gap-3">
      {loading && (
        <div className="mt-[-10px]">
          {["loader-a", "loader-b", "loader-c", "loader-d", "loader-e"].map(
            (item) => (
              <BFLSkeleton key={item} height={20} classes="mb-3" />
            ),
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          {!loading && (
            <>
              <h1 className="text-lg font-medium leading-[22px] uppercase">{compName} {content?.stockPriceText ?? "Share price"}</h1>
              <p className="text-sm leading-[17px]">{symbol}</p>
            </>
          )}
        </div>

        <SwitchExchange
          buttonText={{ BSE: content?.bseText, NSE: content?.nseText }}
          onReady={(val) => setLoading(val)}
          loading={loading}
          nseToken={nseToken}
          bseToken={bseToken}
        />
      </div>

      {!loading && (
        <>
          <div className="flex items-center">
            <span className="text-xs leading-[14px] text-gray-600 md:text-sm md:leading-[17px]">
              {`${mktCapType} ${mktCapType ? "|" : ""} ${ind}`}
            </span>
          </div>

          <div className="flex justify-between">
            <div className="flex flex-col items-start gap-2">
              <div className="text-xl leading-[34px]">{"₹" + price}</div>
              <div className={`text-${chgColor} text-sm leading-[17px]`}>
                {chgSymbol}
                {chg} ({chgSymbol}
                {chgPer + "%"})
                <Conditional if={chg < 0}>
                  <i className="icomoon before:content-['\e981']"></i>
                </Conditional>
                <Conditional if={chg >= 0}>
                  <i className="icomoon before:content-['\e980']"></i>
                </Conditional>
              </div>
            </div>

            <DematAccount
              openDematButtonText={content?.openDematButtonText}
              resumeJourneyText={content?.resumeJourneyText}
              showUnifiedCt={stringToBoolean(content?.showUnifiedCt ?? "")}
              compName={compName ?? ""}
              exchId={exchId}
              formattedSymbol={formattedSymbol}
              nseToken={nseToken}
              bseToken={bseToken}
              showTradeNow={stringToBoolean(content?.showTradeNow)}
              showBuySellButtons={stringToBoolean(content?.showBuySellButtons)}
              buyText={content?.buyText}
              sellText={content?.sellText}
              headerName={content?.headerName}
              bfslPwaBaseUrl={content?.bfslPwaBaseUrl}
              utmSourceApp={content?.utmSourceApp}
              utmSourceWeb={content?.utmSourceWeb}
            />
          </div>

          <div className="text-xs leading-[14px] text-gray-600">
            <Conditional if={!ltt}>
              <>{content?.stockInfoNoteText || ""}</>
            </Conditional>
            <Conditional if={doesExist(ltt)}>
              <>
                {content?.stockInfoNoteText} {formatDateTime(ltt)}
              </>
            </Conditional>
          </div>
        </>
      )}
    </div>
  );
};

export default StockInfo;
